import { useState } from 'react'

import { Form } from '@tribeplatform/react-components/Form'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import { useUpdateMember } from '@tribeplatform/react-sdk/hooks'
import { ActionPanel } from '@tribeplatform/react-ui-kit/ActionPanel'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Card } from '@tribeplatform/react-ui-kit/Card'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

export const ChangePassword = () => {
  const { $t } = useI18n()
  const { mutateAsync: updatePassword, isLoading: updatePasswordLoading } =
    useUpdateMember()
  const [expanded, setExpanded] = useState(false)

  if (!expanded) {
    return (
      <ActionPanel
        title={$t({ defaultMessage: 'Password', id: 'Generics.Password' })}
        placement="trailing-top"
        description={$t({
          defaultMessage: 'Protect your account with a secure password.',
          id: 'Settings.Password.Description',
        })}
      >
        <Button
          variant="secondaryNeutral"
          size="lg"
          onClick={() => setExpanded(true)}
        >
          <T defaultMessage="Change password" id="Settings.Password.Change" />
        </Button>
      </ActionPanel>
    )
  }

  return (
    <Card>
      <Card.Header
        title={<T defaultMessage="Password" id="Generics.Password" />}
        description={
          <T
            defaultMessage="Please enter your current password to change it."
            id="Settings.Password.Change.Description"
          />
        }
      />
      <Card.Content>
        <Form<{
          currentPassword: string
          newPassword: string
          newPasswordConfirm: string
        }>
          onSubmit={async (data, methods) => {
            const { currentPassword, newPassword, newPasswordConfirm } = data

            if (newPassword !== newPasswordConfirm) {
              methods.setError('newPasswordConfirm', {
                message: $t({
                  defaultMessage: 'The passwords do not match',
                  id: 'Settings.Password.Validation.DoNotMatch',
                }),
                type: 'validate',
              })
              return
            }

            await updatePassword({
              input: {
                currentPassword,
                newPassword,
              },
            })

            toast({
              title: $t({
                defaultMessage: 'Password updated successfully',
                id: 'Settings.Password.Changed',
              }),
              status: 'success',
            })
            setExpanded(false)
          }}
        >
          <div className="flex flex-col space-y-5">
            <Form.Input
              type="password"
              name="currentPassword"
              label={
                <T
                  defaultMessage="Current password"
                  id="Settings.Password.Input.Current"
                />
              }
              helperText={
                <T
                  defaultMessage="Please enter your current password."
                  id="Settings.Password.Input.Current.Helper"
                />
              }
            />
            <Form.Input
              type="password"
              name="newPassword"
              label={
                <T
                  defaultMessage="New password"
                  id="Settings.Password.Input.New"
                />
              }
              helperText={
                <T
                  defaultMessage="Please make sure your password includes at least 10 characters, 1 uppercase letter, 1 number, and 1 symbol (!, $, @, *, etc)."
                  id="Settings.Password.Input.New.Helper"
                />
              }
            />
            <Form.Input
              type="password"
              name="newPasswordConfirm"
              label={
                <T
                  defaultMessage="Confirm new password"
                  id="Settings.Password.Input.ConfirmPassword"
                />
              }
              helperText={
                <T
                  defaultMessage="Re-enter your new password for verification."
                  id="Settings.Password.Input.ConfirmPassword.Helper"
                />
              }
            />
            <Form.Actions>
              <Button type="submit" loading={updatePasswordLoading} size="lg">
                <T defaultMessage="Update" id="Generics.Update" />
              </Button>
              <Button
                variant="secondaryNeutral"
                onClick={() => setExpanded(false)}
              >
                <T defaultMessage="Cancel" id="Generics.Cancel" />
              </Button>
            </Form.Actions>
          </div>
        </Form>
      </Card.Content>
    </Card>
  )
}
